import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, 
    List, ListItem, ListItemText, Divider, Box, Chip, Checkbox, FormControlLabel,
    Alert, Stack
} from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function PrescriptionAutoFillModal({ 
    open, 
    onClose, 
    dbMedicines = [], 
    customMedicines = [], 
    appointmentDate = '',
    onConfirm 
}) {
    const [selectedDbMedicines, setSelectedDbMedicines] = useState([]);
    const [selectedCustomMedicines, setSelectedCustomMedicines] = useState([]);

    useEffect(() => {
        if (open) {
            setSelectedDbMedicines(dbMedicines.map((_, idx) => idx));
            setSelectedCustomMedicines(customMedicines.map((_, idx) => idx));
        }
    }, [open, dbMedicines, customMedicines]);

    const handleDbMedicineToggle = (index) => {
        setSelectedDbMedicines(prev => 
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleCustomMedicineToggle = (index) => {
        setSelectedCustomMedicines(prev => 
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleSelectAllDb = () => {
        if (selectedDbMedicines.length === dbMedicines.length) {
            setSelectedDbMedicines([]);
        } else {
            setSelectedDbMedicines(dbMedicines.map((_, idx) => idx));
        }
    };

    const handleSelectAllCustom = () => {
        if (selectedCustomMedicines.length === customMedicines.length) {
            setSelectedCustomMedicines([]);
        } else {
            setSelectedCustomMedicines(customMedicines.map((_, idx) => idx));
        }
    };

    const handleConfirm = () => {
        const selectedDb = selectedDbMedicines.map(idx => dbMedicines[idx]);
        const selectedCustom = selectedCustomMedicines.map(idx => customMedicines[idx]);
        onConfirm(selectedDb, selectedCustom);
    };

    const getSchedules = (medicine) => {
        if (medicine?.doseSchedules?.length > 0) {
            return medicine.doseSchedules;
        }
    
        // fallback to old format
        return [{
            dosage: medicine?.dosage || { morning: 0, noon: 0, evening: 0, night: 0 },
            duration: medicine?.duration || 0,
            frequency: medicine?.frequency || "Daily",
            foodInstruction: medicine?.foodInstruction || "After Food",
        }];
    };

    const formatSchedules = (medicine) => {
        const schedules = getSchedules(medicine);
      
        return schedules.map((s, idx) => {
          const d = s.dosage || {};
          const dosageStr = `${d.morning || 0}-${d.noon || 0}-${d.evening || 0}-${d.night || 0}`;
          return `#${idx + 1}: ${dosageStr} (${s.duration || 0}d, ${s.frequency || "Daily"})`;
        }).join(" | ");
    };

    const formatQuantityBreakdown = (medicine) => {
        const schedules = getSchedules(medicine);
        const qty = medicine.quantity;
      
        let totalUnits = 0;
      
        schedules.forEach((s) => {
          const dailyDose = Object.values(s.dosage || {}).reduce(
            (sum, val) => sum + (parseFloat(val) || 0),
            0
          );
      
          totalUnits += dailyDose * (s.duration || 0);
        });
      
        if (totalUnits > 0) {
          return `${qty} units (${totalUnits} total)`;
        }
      
        return `${qty} units`;
      };

    const totalMedicines = dbMedicines.length + customMedicines.length;
    const totalSelected = selectedDbMedicines.length + selectedCustomMedicines.length;

    if (totalMedicines === 0) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.lighter' }}>
                <MedicationIcon color="primary" />
                <Box>
                    <Typography variant="h5">Prescription Found</Typography>
                    {appointmentDate && (
                        <Typography variant="body2" color="text.secondary">
                            From appointment on {appointmentDate}
                        </Typography>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Select the medicines you want to add to the invoice. Quantities are calculated based on dosage and duration.
                </Alert>

                {dbMedicines.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                                <Typography variant="h6">Medicines Found in Database ({dbMedicines.length})</Typography>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={selectedDbMedicines.length === dbMedicines.length}
                                        indeterminate={selectedDbMedicines.length > 0 && selectedDbMedicines.length < dbMedicines.length}
                                        onChange={handleSelectAllDb}
                                        size="small"
                                    />
                                }
                                label="Select All"
                            />
                        </Stack>
                        <List dense sx={{ bgcolor: 'success.lighter', borderRadius: 1 }}>
                            {dbMedicines.map((medicine, index) => (
                                <Box key={index}>
                                    <ListItem 
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleDbMedicineToggle(index)}
                                    >
                                        <Checkbox 
                                            checked={selectedDbMedicines.includes(index)}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => handleDbMedicineToggle(index)}
                                            size="small"
                                        />
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle1" fontWeight="medium">
                                                    {medicine.name || medicine.medicine_name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Qty:</strong> {formatQuantityBreakdown(medicine)} | 
                                                        <strong> Schedule:</strong> {formatSchedules(medicine)}
                                                    </Typography>
                                                    {medicine.discount > 0 && (
                                                        <Chip label={`${medicine.discount}% off`} size="small" color="success" sx={{ mt: 0.5 }} />
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < dbMedicines.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </List>
                    </Box>
                )}

                {customMedicines.length > 0 && (
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningAmberIcon color="warning" fontSize="small" />
                                <Typography variant="h6">Custom Medicines - Not in Database ({customMedicines.length})</Typography>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={selectedCustomMedicines.length === customMedicines.length}
                                        indeterminate={selectedCustomMedicines.length > 0 && selectedCustomMedicines.length < customMedicines.length}
                                        onChange={handleSelectAllCustom}
                                        size="small"
                                    />
                                }
                                label="Select All"
                            />
                        </Stack>
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            These medicines will be added as placeholders. You&apos;ll need to select the actual medicine from the database.
                        </Alert>
                        <List dense sx={{ bgcolor: 'warning.lighter', borderRadius: 1 }}>
                            {customMedicines.map((medicine, index) => (
                                <Box key={index}>
                                    <ListItem 
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleCustomMedicineToggle(index)}
                                    >
                                        <Checkbox 
                                            checked={selectedCustomMedicines.includes(index)}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => handleCustomMedicineToggle(index)}
                                            size="small"
                                        />
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {medicine.name || medicine.medicine_name}
                                                    </Typography>
                                                    <Chip label="Not in DB" size="small" color="warning" />
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Qty:</strong> {formatQuantityBreakdown(medicine)} | 
                                                        <strong> Schedule:</strong> {formatSchedules(medicine)}
                                                    </Typography>
                                                    {medicine.instructions && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                                            Instructions: {medicine.instructions}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < customMedicines.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                    {totalSelected} of {totalMedicines} medicine(s) selected
                </Typography>
                <Box>
                    <Button onClick={onClose} variant="outlined" color="inherit" sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirm} 
                        variant="contained" 
                        color="primary"
                        disabled={totalSelected === 0}
                    >
                        Add Selected to Invoice
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

PrescriptionAutoFillModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    dbMedicines: PropTypes.array,
    customMedicines: PropTypes.array,
    appointmentDate: PropTypes.string,
    onConfirm: PropTypes.func.isRequired
};

export default PrescriptionAutoFillModal;